import { AfterViewInit, Component, OnInit } from '@angular/core';
import { PaymentGatewayProviderDto } from '../../_core/_model/payment-admin';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { ApiService } from '../../_core/_services/api.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { Modal } from 'bootstrap';


@Component({
  selector: 'app-payment-gateway-providers',
  standalone: false,
  templateUrl: './payment-gateway-providers.html',
  styleUrls: ['./payment-gateway-providers.css']
})
export class PaymentGatewayProviders implements OnInit, AfterViewInit {
  loading = false;
  submitting = false;
  modalSubmitting = false;

  providers: PaymentGatewayProviderDto[] = [];
  filteredProviders: PaymentGatewayProviderDto[] = [];
  search = '';

  editingId: string | null = null;

  editModal?: Modal;

  createForm: FormGroup;
  editForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private notify: NotificationService
  ) {
    this.createForm = this.fb.group({
      code: ['', Validators.required],
      name: ['', Validators.required],
      type: ['Checkout', Validators.required]
    });

    this.editForm = this.fb.group({
      code: ['', Validators.required],
      name: ['', Validators.required],
      type: ['Checkout', Validators.required],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadProviders();
  }

  ngAfterViewInit(): void {
    const el = document.getElementById('editProviderModal');
    if (!el) return;

    this.editModal = Modal.getOrCreateInstance(el);
  }

  loadProviders(): void {
    this.loading = true;
    this.api.getPaymentGatewayProviders().subscribe({
      next: (res) => {
        this.providers = res.data || [];
        this.applyFilter();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to load providers',
        'Error'
      ),
      complete: () => this.loading = false
    });
  }

  applyFilter(): void {
    const q = this.search.trim().toLowerCase();

    this.filteredProviders = !q
      ? [...this.providers]
      : this.providers.filter(x =>
          x.code.toLowerCase().includes(q) ||
          x.name.toLowerCase().includes(q) ||
          x.type.toLowerCase().includes(q)
        );
  }

  create(): void {
    if (this.createForm.invalid) return;

    this.submitting = true;

    const payload = {
      code: (this.createForm.value.code || '').trim().toUpperCase(),
      name: (this.createForm.value.name || '').trim(),
      type: this.createForm.value.type || 'Checkout'
    };

    this.api.createPaymentGatewayProvider(payload).subscribe({
      next: () => {
        this.notify.showSuccess('Provider created successfully', 'Success');
        this.createForm.reset({ code: '', name: '', type: 'Checkout' });
        this.loadProviders();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to create provider',
        'Error'
      ),
      complete: () => this.submitting = false
    });
  }

  openEditModal(item: PaymentGatewayProviderDto): void {
    this.editingId = item.id;
    this.editForm.patchValue({
      code: item.code,
      name: item.name,
      type: item.type,
      isActive: item.isActive
    });
    this.editModal?.show();
  }

  saveEdit(): void {
    if (!this.editingId || this.editForm.invalid) return;

    this.modalSubmitting = true;

    const payload = {
      code: (this.editForm.value.code || '').trim().toUpperCase(),
      name: (this.editForm.value.name || '').trim(),
      type: this.editForm.value.type || 'Checkout',
      isActive: !!this.editForm.value.isActive
    };

    this.api.updatePaymentGatewayProvider(this.editingId, payload).subscribe({
      next: () => {
        this.notify.showSuccess('Provider updated successfully', 'Success');
        this.editModal?.hide();
        this.loadProviders();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to update provider',
        'Error'
      ),
      complete: () => this.modalSubmitting = false
    });
  }

  toggleStatus(item: PaymentGatewayProviderDto): void {
    this.api.togglePaymentGatewayProviderStatus(item.id).subscribe({
      next: () => {
        this.notify.showSuccess('Provider status updated successfully', 'Success');
        this.loadProviders();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to update provider status',
        'Error'
      )
    });
  }
}
